/**
 * Logger Utility for Ricald Frontend
 *
 * Provides environment-aware logging that:
 * - Only outputs debug/info logs in development
 * - Always logs errors and warnings
 * - Supports structured logging with context
 * - Strips debug logs in production builds
 * - Integrates with Sentry for error/warning tracking
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *
 *   logger.debug('Fetching prospects', { page: 1 });
 *   logger.info('User logged in', { userId: '123' });
 *   logger.warn('API rate limit approaching', { remaining: 10 });
 *   logger.error('Failed to load data', error, { endpoint: '/api/prospects' });
 */

import { addBreadcrumb, captureException, captureMessage } from '@/config/sentry';

interface LogContext {
  [key: string]: unknown;
}

const isDev = import.meta.env.DEV;

/**
 * Format log message with optional context
 */
function formatMessage(message: string, context?: LogContext): string {
  if (!context || Object.keys(context).length === 0) {
    return message;
  }
  return `${message} ${JSON.stringify(context)}`;
}

/**
 * Get timestamp for logs
 */
function getTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Logger with environment-aware behavior
 */
export const logger = {
  /**
   * Debug logs - only in development
   * Use for detailed debugging info that's not needed in production
   */
  debug: (message: string, context?: LogContext): void => {
    if (isDev) {
      console.log(`[${getTimestamp()}] [DEBUG] ${formatMessage(message, context)}`);
    }
  },

  /**
   * Info logs - only in development
   * Use for general information about app state
   */
  info: (message: string, context?: LogContext): void => {
    if (isDev) {
      console.info(`[${getTimestamp()}] [INFO] ${formatMessage(message, context)}`);
    }
  },

  /**
   * Warning logs - always logged
   * Use for potential issues that don't break functionality
   * Also sent to Sentry as a message with warning level
   */
  warn: (message: string, context?: LogContext): void => {
    console.warn(`[${getTimestamp()}] [WARN] ${formatMessage(message, context)}`);

    // Send to Sentry as a warning breadcrumb (not a full message to avoid noise)
    addBreadcrumb('logger', message, context as Record<string, unknown>, 'warning');
  },

  /**
   * Error logs - always logged
   * Use for errors that need attention
   * Errors are captured in Sentry for tracking
   */
  error: (message: string, error?: unknown, context?: LogContext): void => {
    const errorDetails = error instanceof Error
      ? { name: error.name, message: error.message, stack: error.stack }
      : error;

    const fullContext = {
      ...context,
      ...(errorDetails ? { error: errorDetails } : {}),
    };

    console.error(`[${getTimestamp()}] [ERROR] ${formatMessage(message, fullContext)}`);

    // Capture error in Sentry
    if (error instanceof Error) {
      captureException(error, { message, ...context });
    } else if (error) {
      captureMessage(message, 'error', { error: errorDetails, ...context });
    } else {
      captureMessage(message, 'error', context as Record<string, unknown>);
    }
  },

  /**
   * Log API request (debug level)
   */
  apiRequest: (method: string, url: string, context?: LogContext): void => {
    if (isDev) {
      console.log(`[${getTimestamp()}] [API] ${method} ${url}`, context || '');
    }
  },

  /**
   * Log API response (debug level)
   */
  apiResponse: (method: string, url: string, status: number, durationMs?: number): void => {
    if (isDev) {
      const duration = durationMs ? ` (${durationMs}ms)` : '';
      console.log(`[${getTimestamp()}] [API] ${method} ${url} → ${status}${duration}`);
    }
  },

  /**
   * Log WebSocket events (debug level)
   */
  ws: (event: string, context?: LogContext): void => {
    if (isDev) {
      console.log(`[${getTimestamp()}] [WS] ${event}`, context || '');
    }
  },

  /**
   * Group logs for readability (debug level)
   */
  group: (label: string, fn: () => void): void => {
    if (isDev) {
      console.group(`[${getTimestamp()}] ${label}`);
      fn();
      console.groupEnd();
    }
  },

  /**
   * Time an operation (debug level)
   */
  time: (label: string): void => {
    if (isDev) {
      console.time(label);
    }
  },

  timeEnd: (label: string): void => {
    if (isDev) {
      console.timeEnd(label);
    }
  },
};

export default logger;
